/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Divider } from "@mui/material";
import ContentShell from "components/ContentShell";
import FilePicker from "components/FilePicker";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextareaInput } from "components/FormTextArea";
import { SimpleTextInput } from "components/FormTextInput";
import SearchableInput from "components/SearchableInput";
import TranslationButtons from "components/TranslationButtons";
import configData from "config/config.json";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import {
  createNotification,
  getNotificationInfo,
  updateNotification,
} from "services/notificationService";
import sessionHelper from "services/sessionHelper";
import { uploadImageV2 } from "services/uploadService";
import useStyles from "styles/notifications/notificationEditor";
import { defaultFileType } from "utils/constants";
import { notificationValidation } from "utils/validation";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";

interface NotificationDetails {
  title: string;
  titleTrans: Record<string, any>;
  body: string;
  bodyTrans: Record<string, any>;
  isActive: boolean;
  territory: string;
  operators: any[];
  imageUrl?: string;
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  liveDate?: string;
}

const _notificationDetails: NotificationDetails = {
  title: "",
  titleTrans: {},
  body: "",
  bodyTrans: {},
  isActive: false,
  territory: "",
  operators: [],
  //   fileType: "/png",
};

const NotificationEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [notificationDetails, setNotificationDetails] =
    useState(_notificationDetails);
  const [modalData, setModalData] = useState({
    modal: "",
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });
  const [image, setImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);

  const { open, isOpen, close } = useOpenClose();

  const cancel = () => navigate(configData.routes.notifications.notifications);

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(notificationDetails?.[field]).map(
      ([key, value]) => {
        return {
          lang: key,
          inputValue: value,
        };
      }
    );
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const submit = async () => {
    try {
      setLoading(true);
      await notificationValidation.validate(notificationDetails);
      let response;
      if (id === "create") {
        response = await createNotification(notificationDetails);
      } else {
        const { id, createdAt, updatedAt, imageUrl, liveDate, ...rest } =
          notificationDetails;
        response = await updateNotification(rest);
      }
      if (image && response?.data.fileUploadUrl) {
        const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.NOTIFICATION];
        await uploadImageV2({
          data: image,
          uploadUrl: response?.data.fileUploadUrl,
          resize: true,
          resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
          resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
        });
      }
      showSnackbar(response.message, "success");
      setLoading(false);
      navigate(configData.routes.notifications.notifications);
    } catch (error) {
      if (error?.inner)
        error.inner.forEach((_err) => showSnackbar(_err.message, "error"));
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = async (e) => {
    const { name, value } = e?.target;
    let data = {
      ...notificationDetails,
      [name]: name === "isActive" ? !notificationDetails.isActive : value,
    };
    if (["title", "body"].includes(name)) {
      data[`${name}Trans`]["en"] = value;
    }

    setNotificationDetails((state) => ({ ...state, ...data }));
  };

  const handleOperatorChange = (e: any) => {
    const { value } = e.target;
    // Handle SearchableInput format (array of objects with id/label)
    if (Array.isArray(value)) {
      const operatorIds = value.map((item) =>
        typeof item === "object" ? item.id : item
      );
      setNotificationDetails((prev) => ({
        ...prev,
        operators: operatorIds,
      }));
    } else {
      // Handle other input formats
      setNotificationDetails((prev) => ({
        ...prev,
        operators: value ? [value] : [],
      }));
    }
  };

  const fetchNotificationInfo = async () => {
    try {
      setLoading(true);
      const resp = await getNotificationInfo(id);

      let titleTrans = { ...resp.data?.titleTrans };
      let bodyTrans = { ...resp.data?.bodyTrans };

      if (resp?.data?.title && !titleTrans["en"]) {
        titleTrans["en"] = resp?.data?.title;
      } else resp.data.title = titleTrans["en"];
      if (resp?.data?.body && !bodyTrans["en"]) {
        bodyTrans["en"] = resp?.data?.body;
      } else resp.data.body = bodyTrans["en"];

      setNotificationDetails({
        ...resp.data,
        titleTrans,
        bodyTrans,
        operators: resp.data?.operators || [],
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const onSelectImage = (image: any, dimensions?: any) => {
    setImage(image ? image : null);
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setNotificationDetails((state) => ({
      ...state,
      fileType: image ? defaultFileType : null,
    }));
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error"
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setNotificationDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {}
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setNotificationDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setNotificationDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }

    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  useEffect(() => {
    id !== "create" && fetchNotificationInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Notification`}
      loading={loading}
      actions={
        <Box className={(styles as any).buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={(styles as any).container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Title"
              name="title"
              value={notificationDetails.title}
              onChange={onChange}
              button={
                <TranslationButtons
                  field={"titleTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Territory"
              name="territory"
              options={sessionHelper.countryOptions}
              value={notificationDetails.territory}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SearchableInput
              selected={(notificationDetails.operators || []).map(
                (operatorId) => {
                  const operator = sessionHelper.operatorOptions?.find(
                    (op) => op.id === operatorId
                  );
                  return {
                    id: operatorId,
                    label: operator?.label || operatorId,
                  };
                }
              )}
              isMultiSelect={true}
              label={"Operators (Optional)"}
              name={"operators"}
              options={sessionHelper.operatorOptions || []}
              onOptionSelect={handleOperatorChange}
              placeholder="Select operators (optional)"
              loading={loading}
            />
          </Box>
        </Box>
        <Box className={styles.bodyContainer}>
          <Box className={styles.fieldBody}>
            <SimpleTextareaInput
              label="Body"
              name="body"
              value={notificationDetails.body}
              onChange={onChange}
              buttons={
                <TranslationButtons
                  field={"bodyTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
        </Box>
        <Box className={styles.imagePickerContainer}>
          <Divider style={{ marginTop: 10 }} variant="fullWidth" />
          <Box className={styles.imagePickerAndPreviewContainer}>
            <FilePicker
              label={"Select Image"}
              value={image}
              name={"bannerImage"}
              onFileSelect={onSelectImage}
              imageType={ImageType.NOTIFICATION}
              enableDimensionControl={true}
              metaData={{
                note: `Recommended: ${
                  DEFAULT_IMAGE_DIMENSIONS[ImageType.NOTIFICATION].width
                } x ${
                  DEFAULT_IMAGE_DIMENSIONS[ImageType.NOTIFICATION].height
                } pixels. Images will be resized and converted to WebP.`,
              }}
            />
            {notificationDetails?.imageUrl && !image && (
              <Box
                component="img"
                src={notificationDetails?.imageUrl}
                alt={`Image Preview`}
                style={{
                  aspectRatio: "16/9",
                  maxHeight: 200,
                  maxWidth: 400,
                  objectFit: "contain",
                  marginTop: 2,
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
      <ViewLanguageModal
        isMultiline
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        modalData={modalData}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default NotificationEditor;
